// src/utils/integrationStatus.js
// Pure status-detection functions for the Integrations Settings page.
// Each function takes the raw practitioner Firestore doc and returns a status
// descriptor: { state, ...details }.
//
// State enum: 'connected' | 'required' | 'recommended' | 'action_needed' | 'not_connected'

export function getStripeStatus(practitioner) {
  const accountId = practitioner?.stripeConnect?.accountId || '';
  const isMockOrTest =
    !accountId ||
    accountId.startsWith('acct_test_') ||
    accountId.startsWith('acct_mock_');
  if (isMockOrTest) return { state: 'required' };
  return { state: 'connected' };
  // Action Needed states (incomplete onboarding, identity verification pending)
  // are deferred until B17 is resolved.
}

export function getZoomStatus(practitioner) {
  const connected = practitioner?.zoomConnected === true;
  const connectedAt = practitioner?.zoomConnectedAt?.toDate?.() || null;
  if (connected) return { state: 'connected', connectedAt };
  return { state: 'recommended' };
}

export function getGoogleCalendarStatus(practitioner) {
  const connected = practitioner?.calendarConnected === true;
  const connectedAt = practitioner?.calendarConnectedAt?.toDate?.() || null;
  if (connected) return { state: 'connected', connectedAt };
  return { state: 'recommended' };
  // Action Needed for invalid_grant token failures is deferred to B15 — no
  // token-error field is written to Firestore yet, so we can't detect it.
}

export function getConvertKitStatus(practitioner) {
  const connected = practitioner?.convertkitConnected === true;
  const lastSyncAt = practitioner?.convertkitLastSyncAt?.toDate?.() || null;
  if (connected) return { state: 'connected', lastSyncAt };
  return { state: 'not_connected' };
}

export function getGoogleBusinessStatus(practitioner) {
  const connected = practitioner?.googleBusiness?.connected === true;
  const businessName = practitioner?.googleBusiness?.businessName || null;
  if (connected) return { state: 'connected', businessName };
  return { state: 'not_connected' };
}

const SOCIAL_OAUTH_PLATFORMS = ['facebook', 'instagram', 'tiktok'];

export function supportsOAuthVerification(platform) {
  return SOCIAL_OAUTH_PLATFORMS.includes(platform);
}

// Generic per-platform social detection — used for all 7 platforms.
// OAuth-verified state lives at socialConnections.{platform}.connected
// (written by handleFacebookCallback / handleTikTokCallback Cloud Functions).
// Manual URLs: prefer website_published over website_draft so the Integrations
// page reflects what's live on the public site, not in-progress edits. Falls
// back to draft only if nothing has been published yet.
export function getSocialStatus(practitioner, platform) {
  const connected = practitioner?.socialConnections?.[platform]?.connected === true;
  const publishedLinks = practitioner?.website_published?.contactFooter?.socialLinks || {};
  const draftLinks = practitioner?.website_draft?.contactFooter?.socialLinks || {};
  const manualUrl = (publishedLinks[platform] || draftLinks[platform] || '').trim();

  if (connected) return { state: 'connected', verified: true, url: manualUrl || null };
  if (manualUrl) return { state: 'connected', verified: false, url: manualUrl };
  return { state: 'not_connected', verified: false, url: null };
}
